import { useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useImageUpload } from "@/hooks/use-image-upload";
import { ImageIcon, Upload, X, Loader2 } from "lucide-react";

interface HandbookThumbnailFieldProps {
  value: string;
  onChange: (url: string) => void;
  error?: string;
}

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export function HandbookThumbnailField({
  value,
  onChange,
  error,
}: HandbookThumbnailFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading } = useImageUpload({
    category: "handbook-thumbnail",
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await upload(file);
    if (url) onChange(url);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground">
        썸네일 (선택)
      </label>

      {value && (
        <div className="relative w-full overflow-hidden rounded-md border">
          <img
            src={value}
            alt="썸네일 미리보기"
            className="h-40 w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={() => onChange("")}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {!value && (
        <div
          className="flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed transition-colors hover:border-primary hover:bg-muted/50"
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          ) : (
            <ImageIcon className="text-muted-foreground h-8 w-8" />
          )}
          <span className="text-muted-foreground text-xs">
            {isUploading ? "업로드 중..." : "클릭하여 이미지 업로드"}
          </span>
        </div>
      )}

      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... (직접 URL 입력 가능)"
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handleFileSelect}
      />

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
